import { useEffect, useRef } from 'react';

// Face-presence detection — runs entirely in the browser (TensorFlow.js +
// BlazeFace), so there's no server cost and no video ever leaves the
// student's machine for this check. This is targeted behavioral detection
// (is a face visible, is there more than one), not general-purpose AI video
// analysis — it only answers the two questions a proctor actually cares
// about moment-to-moment.
//
// @tensorflow/tfjs + the BlazeFace model are a non-trivial download, so both
// are lazy-loaded (dynamic import) only once an exam is actually active,
// not bundled into the app's main chunk for every visitor.
const useFaceDetection = ({ isActive, videoRef, onViolation }) => {
  const onViolationRef = useRef(onViolation);
  useEffect(() => { onViolationRef.current = onViolation; }, [onViolation]);

  const lastReportedRef = useRef({});
  const COOLDOWN_MS = 15000; // tolerate a brief look-away before flagging it

  const report = (type, description) => {
    const now = Date.now();
    const last = lastReportedRef.current[type] || 0;
    if (now - last < COOLDOWN_MS) return;
    lastReportedRef.current[type] = now;
    onViolationRef.current?.({ type, description, timestamp: new Date().toISOString() });
  };

  useEffect(() => {
    if (!isActive) return;
    let cancelled = false;
    let intervalId = null;
    let model = null;

    (async () => {
      try {
        const [tf, blazeface] = await Promise.all([
          import('@tensorflow/tfjs'),
          import('@tensorflow-models/blazeface'),
        ]);
        await tf.ready();
        model = await blazeface.load();
        if (cancelled) return;

        intervalId = setInterval(async () => {
          const video = videoRef.current;
          if (!video || video.readyState < 2 || !video.videoWidth) return;
          try {
            const predictions = await model.estimateFaces(video, false);
            if (predictions.length === 0) {
              report('NO_FACE_DETECTED', 'No face detected in webcam feed');
            } else if (predictions.length > 1) {
              report('MULTIPLE_FACES_DETECTED', `${predictions.length} faces detected in webcam feed`);
            }
          } catch (e) {
            // A single failed detection tick isn't worth surfacing — the
            // next tick tries again. Only a total load failure (below) is
            // worth giving up on entirely.
          }
        }, 3000);
      } catch (e) {
        // Model failed to load (slow/blocked network, unsupported browser,
        // etc.) — fail open, same philosophy as the rest of this app's
        // best-effort client-side checks (see detectIncognito.js). Face
        // detection is a bonus signal, not something that should block or
        // break the exam if it can't run.
      }
    })();

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [isActive, videoRef]);
};

export default useFaceDetection;
